import React, { useEffect, useState } from "react";
import { PlugZap, Copy, Check, Server, Info } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { Card, Section, Pill } from "./ui";

function CodeBlock({ label, code, id }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-lg border border-subtle bg-[#05090f]">
      <div className="flex items-center justify-between border-b border-subtle px-3 py-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        <button
          data-testid={`copy-${id}`}
          onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
          className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-brand"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="custom-scrollbar overflow-x-auto p-3 font-mono text-[12px] leading-relaxed text-slate-300">{code}</pre>
    </div>
  );
}

export default function Integration() {
  const { session } = useAuth();
  const [cfg, setCfg] = useState(null);

  useEffect(() => { api.integration(session.tenant.id).then(setCfg); }, [session.tenant.id]);

  if (!cfg) return null;

  const curl = `curl ${cfg.base_url}/chat/completions \\
  -H "Authorization: Bearer ${cfg.example_api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"lucidgate-auto","messages":[{"role":"user","content":"Summarise this contract"}]}'`;

  const py = `from openai import OpenAI

client = OpenAI(
    base_url="${cfg.base_url}",
    api_key="${cfg.example_api_key}",
)
resp = client.chat.completions.create(
    model="lucidgate-auto",
    messages=[{"role": "user", "content": "Summarise this contract"}],
)
print(resp.choices[0].message.content)`;

  return (
    <Section
      title="Integration Surface"
      subtitle="Connect existing tools through an OpenAI-compatible endpoint — alongside the LucidGate web UI."
    >
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-xs text-warn">
        <Info className="h-4 w-4" /> Illustrative only — this endpoint is not a live API in this prototype.
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <div className="mb-4 flex items-center gap-2">
            <Server className="h-4 w-4 text-brand" />
            <h3 className="font-display text-sm font-bold">Endpoint configuration</h3>
            <Pill tone="eu" className="ml-auto">EU Gateway</Pill>
          </div>
          <div className="space-y-3">
            <Field label="Base URL" value={cfg.base_url} id="base-url" />
            <Field label="API Key (sample)" value={cfg.example_api_key} id="api-key" mono />
            <Field label="Model" value="lucidgate-auto (policy-routed)" id="model" />
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-eu/30 bg-eu/10 px-3 py-2 text-[11px] text-eu">
            <PlugZap className="h-3.5 w-3.5" /> Traffic through this surface passes the same detection, policy and sovereign routing.
          </div>
        </Card>

        <div className="space-y-4">
          <CodeBlock label="cURL" code={curl} id="curl" />
          <CodeBlock label="Python (openai sdk)" code={py} id="python" />
        </div>
      </div>
    </Section>
  );
}

function Field({ label, value, id, mono }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="flex items-center gap-2 rounded-lg border border-subtle bg-elevated px-3 py-2">
        <span className={`flex-1 truncate text-sm ${mono ? "font-mono" : ""} text-slate-200`} data-testid={`field-${id}`}>{value}</span>
        <button onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }} className="text-slate-400 hover:text-brand">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
